import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/apiClient";
import { getAuthUser, saveAuthUser } from "../utils/auth";
import { getReferralCode, clearReferralCode } from "../utils/referralSession";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTarget = location.state?.from;

  const buildPathFromLocation = (target) => {
    if (!target?.pathname) return "";
    return `${target.pathname}${target.search || ""}${target.hash || ""}`;
  };

  const computeDefaultPath = (user) => {
    const tier = Number(user?.packageTier);
    return Number.isFinite(tier) && tier >= 1
      ? "/dashboard"
      : "/dashboard/packages";
  };

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const googleButtonRef = useRef(null);

  useEffect(() => {
    const existing = getAuthUser();
    if (existing) {
      navigate(computeDefaultPath(existing), { replace: true });
    }
  }, [navigate]);

  const handleGoogleResponse = useCallback(async (response) => {
    if (!response.credential) {
      setStatus("error");
      setMessage("Google authentication failed. Please try again.");
      return;
    }

    setStatus("loading");
    setMessage("");

    try {
      const authResponse = await apiFetch("/auth/google", {
        method: "POST",
        data: {
          credential: response.credential,
          isSignup: false,
        },
      });

      setStatus("success");
      setMessage(authResponse?.message || "Login successful");

      if (authResponse?.user) {
        saveAuthUser(authResponse.user);
      }

      const storedReferralCode = getReferralCode();
      if (storedReferralCode) {
        try {
          const applyResponse = await apiFetch("/users/referral/apply", {
            method: "POST",
            data: { referralCode: storedReferralCode },
          });
          if (applyResponse?.user) {
            saveAuthUser(applyResponse.user);
          }
          clearReferralCode();
        } catch (applyError) {
          if (applyError?.data?.message === "Referral bonus already claimed.") {
            clearReferralCode();
          }
        }
      }

      setTimeout(() => {
        const updatedUser = getAuthUser();
        const fallbackPath = computeDefaultPath(updatedUser || authResponse?.user);
        const redirectPath = redirectTarget
          ? buildPathFromLocation(redirectTarget)
          : fallbackPath;

        navigate(redirectPath || fallbackPath, {
          replace: true,
          state: redirectTarget?.state,
        });
      }, 800);
    } catch (error) {
      setStatus("error");
      let errorMsg = "Google login failed";
      
      if (error.message === "Network request failed" || error.cause) {
        errorMsg = "Unable to connect to server. Please check your internet connection and ensure the backend is running.";
      } else if (error?.data?.message) {
        errorMsg = error.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setMessage(errorMsg);
      console.error("Google login error:", error);
    } finally {
      setStatus((current) => (current === "success" ? "success" : "idle"));
    }
  }, [navigate, redirectTarget]);

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      console.warn('Google Client ID not configured');
      return;
    }

    const initializeGoogleButton = () => {
      if (!googleButtonRef.current) return;

      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleResponse,
        });

        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          width: '100%',
          text: 'continue_with',
        });
      } catch (error) {
        console.error('Failed to initialize Google button:', error);
      }
    };

    if (window.google && window.google.accounts && window.google.accounts.id) {
      initializeGoogleButton();
    } else {
      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          if (window.google && window.google.accounts && window.google.accounts.id) {
            initializeGoogleButton();
          }
        });
        return;
      }

      // Load Google script
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
          initializeGoogleButton();
        }
      };
      script.onerror = () => {
        console.error('Failed to load Google Identity Services script');
      };
      document.head.appendChild(script);
    }
  }, [handleGoogleResponse]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setMessage("");

    if (!formData.email || !formData.password) {
      setStatus("error");
      setMessage("Please provide both email and password.");
      return;
    }

    try {
      const response = await apiFetch("/auth/login", {
        method: "POST",
        data: formData,
      });

      setStatus("success");
      setMessage(response?.message || "Login successful");

      if (response?.user) {
        saveAuthUser(response.user);
      }

      const storedReferralCode = getReferralCode();
      if (storedReferralCode) {
        try {
          const applyResponse = await apiFetch("/users/referral/apply", {
            method: "POST",
            data: { referralCode: storedReferralCode },
          });
          if (applyResponse?.user) {
            saveAuthUser(applyResponse.user);
          }
          clearReferralCode();
        } catch (applyError) {
          if (applyError?.data?.message === "Referral bonus already claimed.") {
            clearReferralCode();
          }
        }
      }

      setTimeout(() => {
        const updatedUser = getAuthUser();
        const fallbackPath = computeDefaultPath(updatedUser || response?.user);
        const redirectPath = redirectTarget
          ? buildPathFromLocation(redirectTarget)
          : fallbackPath;

        navigate(redirectPath || fallbackPath, {
          replace: true,
          state: redirectTarget?.state,
        });
      }, 800);

      //  setTimeout(() => {
      //   navigate("/dashboard");
      // }, 800);
    } catch (error) {
      setStatus("error");
      setMessage(error?.data?.message || "Login failed");
    } finally {
      setStatus((current) => (current === "success" ? "success" : "idle"));
    }
  };

  return (
    <section className="relative w-full min-h-screen overflow-x-hidden bg-black font-[Poppins]">
      <img
        src="images/top.png"
        alt="Top"
        className="pointer-events-none absolute top-0 left-1/2 w-[140%] max-w-none -translate-x-1/2 opacity-40 sm:w-full sm:max-w-2xl sm:opacity-60"
      />
      <img
        src="images/leftcorner.png"
        alt="Left"
        className="pointer-events-none absolute bottom-[-4rem] left-[-6rem] hidden w-64 opacity-40 sm:left-[-3rem] sm:block sm:opacity-70 md:bottom-0"
      />
      <img
        src="images/right-corner.png"
        alt="Right"
        className="pointer-events-none absolute bottom-[-4rem] right-[-6rem] hidden w-64 opacity-40 sm:right-[-3rem] sm:block sm:opacity-70 md:bottom-0"
      />
      <img
        src="images/synergy.png"
        alt="Left Coin"
        className="pointer-events-none absolute left-[-4rem] top-1/3 hidden w-32 opacity-50 sm:block sm:left-6 sm:w-40 sm:opacity-80 md:left-12 md:w-[10rem]"
      />

      <img
        src="images/synergy.png"
        alt="Right Coin"
        className="pointer-events-none absolute right-[-3rem] top-[22%] hidden w-20 opacity-50 sm:block sm:right-10 sm:w-28 sm:opacity-80 md:right-12"
      />

      <div className="relative z-20 flex min-h-screen flex-col">
        <div className="flex items-center justify-between px-4 py-5 sm:px-6">
          <img src="images/login-logo.png" alt="Logo" className="h-8 w-auto sm:h-10" />
          <button
            onClick={() => navigate("/signup")}
            className="rounded-full border border-white/20 px-4 py-1.5 text-sm text-white transition hover:border-white/40 hover:text-purple-300 sm:px-5"
          >
            Sign Up
          </button>
        </div>

        <div className="flex flex-1 items-start justify-center px-4 pb-12 pt-2 sm:items-center sm:px-6 sm:pb-14">
          <div className="w-full max-w-[420px]">
            <form
              onSubmit={handleSubmit}
              className="rounded-3xl bg-gradient-to-br from-white/8 via-white/6 to-white/3 px-5 py-8 backdrop-blur-2xl shadow-[0_32px_80px_rgba(5,2,15,0.55)] sm:px-8"
            >
              <div className="mb-6 text-center">
                <h1 className="text-2xl font-semibold text-transparent bg-gradient-to-r from-[#ECEFF0] to-[#E5C0FF] bg-clip-text sm:text-3xl">
                  Welcome to
                </h1>

                <h2 className="mt-1 text-3xl font-bold text-transparent bg-gradient-to-r from-[#ECEFF0] to-[#E5C0FF] bg-clip-text sm:text-4xl">
                  SynergyCoin
                </h2>

                <p className="mt-2 text-sm text-gray-300 sm:text-base">Login to continue</p>
              </div>

              <div ref={googleButtonRef} className="w-full flex justify-center"></div>

              <div className="my-5 flex items-center gap-4 text-xs uppercase tracking-[0.3rem] text-white/40">
                <span className="h-px flex-1 bg-white/15" />
                or
                <span className="h-px flex-1 bg-white/15" />
              </div>

              {message && (
                <div
                  className={`mb-4 rounded-lg py-2 text-center text-sm ${
                    status === "error"
                      ? "bg-red-900/40 text-red-300"
                      : "bg-green-900/35 text-green-300"
                  }`}
                >
                  {message}
                </div>
              )}

              <label className="mb-1 block text-sm text-gray-200">Email</label>
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                value={formData.email}
                onChange={handleChange}
                className="mb-4 w-full rounded-xl border border-white/10 bg-black/60 px-4 py-3 text-sm text-gray-100 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none"
              />

              <label className="mb-1 block text-sm text-gray-200">Password</label>
              <div className="relative mb-6">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="****************"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-white/10 bg-black/60 px-4 py-3 pr-12 text-sm text-gray-100 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-lg text-gray-300 transition hover:text-white"
                >
                  👁️
                </button>
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full rounded-xl bg-gradient-to-r from-[#7F3FFD] to-[#9F4FFF] py-3 text-sm font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === "loading" ? "Logging in..." : "Log In"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="mt-3 w-full text-center text-sm text-white/80 transition hover:text-purple-300"
              >
                Forgot Password?
              </button>
            </form>

            <p className="mt-6 text-center text-xs text-gray-400">
              By using SynergyCoin, you agree to our terms and policies.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Login;
